import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Switch from 'flarum/common/components/Switch';
import Sortable from 'sortablejs';
import Api from '../utils/Api';
import SchoolEditorModal from '../modal/SchoolEditorModal';
import AssignStaffModal from '../modal/AssignStaffModal';
import SchoolScheduleModal from '../modal/SchoolScheduleModal';

/**
 * v0.5.0v — 校区管理 Tab (CRUD + 拖拽 + 分配教练/销售)
 *
 * 严格按设计图 /Users/zhaozihui/.mavis/agents/mavis/workspace/designs/schools-tab-{desktop,mobile}.html
 * 模板复用: v0.5.0t mobile 双树 + v0.5.0pa 拖拽手柄右侧 + v0.5.0sa Switch 优化
 *
 * 已知差异 (跟辉哥拍板):
 *   - 拖拽手柄: 桌面 hover 显示 / mobile 常亮 (v0.5.0pa)
 *   - Switch: 直接改 this.schools[idx].status + m.redraw, 1 PATCH only (v0.5.0sa)
 *   - 路由: /schools/reorder 必须注册在 /schools/{id} 之前 (extend.php L105)
 */
export default class SchoolsTab extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    // v0.6.48 — 读全局 activeSchoolId 兜底传 modal (辉哥 9/30 12:14+ 反馈)
    //   SchedulerIndexPage L340 已经传 activeSchoolId 给 SchoolsTab, 之前忽略没用
    //   兜底: localStorage 直接读 (即使 caller 没传也能拿到)
    this.activeSchoolId = vnode.attrs.activeSchoolId || localStorage.getItem('ziven_scheduler_active_school_id');
    this.schools = [];
    this.loading = true;
    this.sortableDesktop = null;
    this.sortableMobile = null;
    this.load();
  }
  onremove() {
    this.destroySortables();
  }

  /**
   * v0.18.45 (辉哥 10/21 拍板) — 当前用户能不能**写**校区?
   *
   * ## 为什么需要这个 (本轮引入的, 不是历史遗留)
   *   v0.18.45 把「校区」tab 的**门**从 `scheduler.manageSchools` 换成了**只读**位
   *   `scheduler.viewSchools` (店长组拿的是只读位)。⇒ 店长现在**能打开**这个 tab,
   *   但**不能**建/改/删/排序/分配人员。
   *   ⇒ 本组件必须把**写控件**藏起来 —— 否则店长点「删除」会撞一个 403。
   *   (后端已经挡住了 —— v0.18.45 给 6 个写端点加了 admin 判定 + 范围校验;
   *    这一层是**前端体验**, 让只读用户**根本看不到**那些按钮。)
   *
   * ## 判据
   *   `app.session.user.canManageSchools()` —— 后端 `UserSchedulerPermissions::canManageSchools`
   *   暴露的属性 (前端 getter 见 js/src/forum/index.js)。
   *   ⚠️ **fail-closed**: 取不到 user / getter 不存在 / 调用抛异常 ⇒ 一律当**不能写**。
   */
  canManage() {
    try {
      const u = app.session && app.session.user;
      if (!u) return false;
      const fn = u.canManageSchools;
      if (typeof fn !== 'function') return false;
      return !!fn.call(u);
    } catch (e) {
      return false;
    }
  }
  async load() {
    try {
      this.loading = true;
      this.schools = await Api.list('schools');
    } catch (e) {
      console.error('[Schools] load failed', e);
    } finally {
      this.loading = false;
      m.redraw();
      this.rebuildSortables();
    }
  }

  // 拖拽结束 → 重排 + POST /schools/reorder (v0.5.0p 第二阶段同款)
  async onReorderEnd(evt, mode) {
    if (evt.oldIndex === evt.newIndex) return;
    if (evt.oldIndex == null || evt.newIndex == null) return;
    const [moved] = this.schools.splice(evt.oldIndex, 1);
    this.schools.splice(evt.newIndex, 0, moved);
    m.redraw();
    const ids = this.schools.map(s => s.id);
    try {
      // v0.8.10 — 删掉 const r: 这个返回值原来只喂 console 的 log 调用, 日志删了之后没人用了
      await Api.reorder('schools', ids);
    } catch (e) {
      console.error('[Schools] reorder failed, reverting', e);
      const [back] = this.schools.splice(evt.newIndex, 1);
      this.schools.splice(evt.oldIndex, 0, back);
      m.redraw();
    }
  }
  rebuildSortables() {
    this.destroySortables();
    // v0.18.45 — 只读用户**不挂**拖拽排序 (排序是全站级写操作, 后端限 admin)。
    if (!this.canManage()) return;
    if (!this.schools || this.schools.length < 2) return;
    let attempts = 0;
    const tryMount = () => {
      attempts++;
      const desktopEl = document.querySelector('.ZS-school-grid');
      if (desktopEl && !this.sortableDesktop) {
        this.sortableDesktop = Sortable.create(desktopEl, {
          animation: 150,
          handle: '.ZS-drag-handle',
          ghostClass: 'ZS-sortable-ghost',
          dragClass: 'ZS-sortable-drag',
          onEnd: evt => this.onReorderEnd(evt, 'desktop')
        });
      }
      const mobileEl = document.querySelector('.ZS-school-list-mobile');
      if (mobileEl && !this.sortableMobile) {
        this.sortableMobile = Sortable.create(mobileEl, {
          animation: 150,
          handle: '.ZS-drag-handle',
          ghostClass: 'ZS-sortable-ghost',
          dragClass: 'ZS-sortable-drag',
          onEnd: evt => this.onReorderEnd(evt, 'mobile')
        });
      }
      if (!desktopEl && attempts < 15) setTimeout(tryMount, 100);
    };
    setTimeout(tryMount, 0);
  }
  destroySortables() {
    if (this.sortableDesktop) {
      try {
        this.sortableDesktop.destroy();
      } catch (e) {}
      this.sortableDesktop = null;
    }
    if (this.sortableMobile) {
      try {
        this.sortableMobile.destroy();
      } catch (e) {}
      this.sortableMobile = null;
    }
  }
  openEditor(school) {
    app.modal.show(SchoolEditorModal, {
      school: school || null,
      onClose: () => this.load()
    });
  }

  // v0.6.48 — 兜底 caller 传 school 没 id 时用全局 activeSchoolId (辉哥 9/30 12:14+ 反馈)
  //   修前 caller 传 school 但 school.id undefined → modal URL /schools/undefined/staff → 422
  //   修后 modal 自己读 globalActiveSchoolId 兜底 (兜底逻辑在 modal oninit L21-50, 这里 caller 仍传 school 方便 title 显示 name)
  //   schools 列表传给 modal, modal title() L42-58 从 this.attrs.schools 找 school.name
  openAssignStaff(school) {
    let resolvedSchool = school;
    if (!resolvedSchool && this.activeSchoolId) {
      // caller 没传 school (e.g. 旧 caller 路径), 用 this.activeSchoolId 兜底构造最小 school 对象
      resolvedSchool = {
        id: parseInt(this.activeSchoolId, 10),
        name: null
      };
    }
    app.modal.show(AssignStaffModal, {
      school: resolvedSchool,
      schools: this.schools,
      // 传 schools 列表给 modal, modal title() 用它找 school.name
      onClose: () => this.load()
    });
  }

  // v0.17.58 — 打开课表配置 modal
  //   没配过课表的校区**照样能点进去** (modal 内部走空态), 不在这里禁用 ——
  //   「还没配置」是正常业务状态, 不是错误态。
  openSchedule(school) {
    app.modal.show(SchoolScheduleModal, {
      school,
      onSaved: () => this.load()
    });
  }

  // v0.5.0sa Switch 优化: 直接改 this.schools[idx].status + m.redraw, 1 PATCH only
  onSwitchChange(s, checked) {
    const newStatus = checked ? 'enabled' : 'disabled';
    Api.update('schools', s.id, {
      status: newStatus
    }).then(() => {
      const idx = this.schools.findIndex(x => x.id === s.id);
      if (idx >= 0) {
        this.schools[idx].status = newStatus;
        m.redraw();
      }
    }).catch(e => {
      console.error('[Schools] Switch update failed', e);
    });
  }
  async remove(s) {
    if (!confirm('确认删除校区 "' + s.name + '"? 关联的教室/学员/教练分配会被清空')) return;
    try {
      await Api.delete('schools', s.id);
      await this.load();
    } catch (e) {
      alert('删除失败: ' + (e?.message || ''));
    }
  }

  // avatar 颜色按 sort_order 循环: 蓝 / 绿 / 橙 (跟 RoomsTab 同款)
  avatarClass(school) {
    const palette = ['', 'green', 'orange', '', '', ''];
    return palette[(school.sort_order || 0) % 3];
  }
  view() {
    const schoolData = this.schools.map(s => {
      const disabled = s.status === 'disabled';
      return {
        school: s,
        disabled
      };
    });

    // 🔴 v0.18.48 追加 (Mavis 审出来的) — 汇总行两个数**各自归位**, ⛔ 不能再共用一个数据源:
    //   · 「N 名教练」 = `assigned_coach_count` —— RoleResolver 按 role='coach' 判的**真教练数**
    //   · 「M 人已分配」= `coach_count` —— **分配到该校的所有人**(教练+销售+店长+学员), 原语义不变
    //   ⚠️ 改前这两个位置的数据源是**反的**: 「教练组」读 coach_count(分配人数)、
    //      「已分配」读 assigned_coach_count(教练数) ⇒ 生产上会渲染成「20 名教练组 · 3 已分配」
    //      (教练比已分配还多, 明显自相矛盾)。
    const totalCoaches = this.schools.reduce((sum, s) => sum + (s.assigned_coach_count || 0), 0);
    const totalAssigned = this.schools.reduce((sum, s) => sum + (s.coach_count || 0), 0);
    return m("div", null, m("div", {
      className: "ZS-school-title-row"
    }, m("div", {
      className: "ZS-toolbar-left"
    }, m("div", {
      className: "ZS-school-title"
    }, m("i", {
      className: "fas fa-location-dot",
      style: "color:var(--zs-brand-2d7be5, #2d7be5);"
    }), "\u6821\u533A\u7BA1\u7406"), m("span", {
      className: "ZS-total-text"
    }, "\u5171 ", this.schools.length, " \u4E2A\u6821\u533A \xB7 ", totalCoaches, " \u540D\u6559\u7EC3 \xB7 ", totalAssigned, " \u4EBA\u5DF2\u5206\u914D")), m("div", {
      className: "ZS-toolbar-right"
    }, this.canManage() && m("button", {
      className: "ZS-btn primary",
      onclick: () => this.openEditor(null)
    }, m("i", {
      className: "fas fa-plus"
    }), " \u65B0\u5EFA\u6821\u533A"))), this.loading ? m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D...") : m("div", null, m("div", {
      className: "ZS-school-grid"
    }, schoolData.map(_ref => {
      let {
        school: s,
        disabled
      } = _ref;
      return m("div", {
        className: 'ZS-school-card ' + (disabled ? 'disabled' : ''),
        key: s.id
      }, this.canManage() && m("span", {
        className: "ZS-drag-handle",
        title: "\u62D6\u52A8\u6392\u5E8F"
      }, "\u22EE\u22EE"), m("div", {
        className: "ZS-school-head"
      }, m("div", {
        className: 'ZS-school-avatar ' + this.avatarClass(s)
      }, m("i", {
        className: "fas fa-building"
      })), m("div", {
        className: "ZS-school-name-block"
      }, m("div", {
        className: "ZS-school-name"
      }, s.name))), m("div", {
        className: "ZS-school-meta"
      }, disabled ? m("span", {
        className: "ZS-school-tag gray"
      }, m("i", {
        className: "fas fa-circle-pause"
      }), " \u5DF2\u505C\u7528") : m("span", {
        className: "ZS-school-tag green"
      }, m("i", {
        className: "fas fa-check"
      }), " \u542F\u7528\u4E2D"), m("span", {
        className: "ZS-school-tag gray"
      }, m("i", {
        className: "fas fa-users"
      }), " ", s.student_count || 0, " \u5728\u8BFB\u5B66\u5458"), m("span", {
        className: "ZS-school-tag gray"
      }, m("i", {
        className: "fas fa-house"
      }), " ", s.room_count || 0, " \u6559\u5BA4"), s.allow_self_booking && m("span", {
        className: "ZS-school-tag selfbook"
      }, m("i", {
        className: "fas fa-user-clock"
      }), " \u53EF\u81EA\u4E3B\u7EA6\u8BFE")), m("div", {
        className: "ZS-school-staff-block"
      }, m("div", {
        className: "ZS-school-staff-row",
        onclick: this.canManage() ? () => this.openAssignStaff(s) : null
      }, m("i", {
        className: "fas fa-person-chalkboard",
        style: "color:var(--coach,var(--zs-success-10b981, #10b981));"
      }), m("span", null, "\u6559\u7EC3 ", m("strong", null, s.assigned_coach_count || 0), " \u540D"), m("span", {
        style: "opacity:0.35;margin:0 2px;"
      }, "\xB7"), m("i", {
        className: "fas fa-briefcase",
        style: "color:var(--sales,var(--zs-warning-f59e0b, #f59e0b));"
      }), m("span", null, "\u9500\u552E ", m("strong", null, s.assigned_sales_count || 0), " \u540D"), m("span", {
        style: "opacity:0.35;margin:0 2px;"
      }, "\xB7"), m("i", {
        className: "fas fa-user-tie",
        style: "color:var(--manager,var(--zs-purple-8b5cf6, #8b5cf6));"
      }), m("span", null, "\u5E97\u957F ", m("strong", null, s.assigned_manager_count || 0), " \u540D"), this.canManage() ? m("span", {
        style: "margin-left:auto;font-size:11px;opacity:0.7;"
      }, "\u70B9\u51FB\u5206\u914D \u2192") : null)), m("div", {
        className: "ZS-school-actions"
      }, m("button", {
        className: "ZS-btn secondary",
        style: "padding:6px 12px;font-size:12px;",
        onclick: () => this.openSchedule(s)
      }, m("i", {
        className: "far fa-clock"
      }), " \u8BFE\u8868"), this.canManage() && m("button", {
        className: "ZS-btn secondary",
        style: "padding:6px 12px;font-size:12px;",
        onclick: () => this.openEditor(s)
      }, m("i", {
        className: "fas fa-pen-to-square"
      }), " \u7F16\u8F91"), this.canManage() && m("button", {
        className: "ZS-btn secondary",
        style: "padding:6px 12px;font-size:12px;color:var(--zs-danger-ff3b30, #ff3b30);",
        onclick: () => this.remove(s)
      }, m("i", {
        className: "fas fa-trash"
      }), " \u5220\u9664"), m("span", {
        className: "ZS-actions-spacer"
      }), this.canManage() && m('[', null, m("span", {
        className: "ZS-switch-label"
      }, disabled ? '停用' : '启用'), m(Switch, {
        state: !disabled,
        onchange: checked => this.onSwitchChange(s, checked)
      }))));
    }), this.schools.length === 0 && m("div", {
      className: "ZS-school-empty"
    }, m("i", {
      className: "fas fa-location-dot",
      style: "font-size:32px;color:var(--zs-text-3-5e6673, #5e6673);"
    }), m("div", {
      style: "margin-top:12px;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u6682\u65E0\u6821\u533A, \u70B9\u51FB\u53F3\u4E0A\u89D2 \"\u65B0\u5EFA\u6821\u533A\" \u5F00\u59CB"))), m("div", {
      className: "ZS-school-list-mobile"
    }, schoolData.map(_ref2 => {
      let {
        school: s,
        disabled
      } = _ref2;
      return m("div", {
        className: 'ZS-school-card-mobile ' + (disabled ? 'disabled' : ''),
        key: 'm-' + s.id
      }, this.canManage() && m("span", {
        className: "ZS-drag-handle",
        title: "\u62D6\u52A8\u6392\u5E8F"
      }, "\u22EE\u22EE"), m("div", {
        className: 'ZS-school-color-bar-mobile ' + this.avatarClass(s)
      }), m("div", {
        className: "ZS-school-head-mobile"
      }, m("div", {
        className: "ZS-school-name"
      }, s.name), m("div", {
        className: "ZS-school-meta-mobile"
      }, disabled ? m("span", {
        className: "ZS-school-tag gray"
      }, m("i", {
        className: "fas fa-circle-pause"
      }), " \u5DF2\u505C\u7528") : m("span", {
        className: "ZS-school-tag green"
      }, m("i", {
        className: "fas fa-check"
      }), " \u542F\u7528\u4E2D"), m("span", {
        className: "ZS-school-tag gray"
      }, m("i", {
        className: "fas fa-users"
      }), " ", s.student_count || 0), m("span", {
        className: "ZS-school-tag gray"
      }, m("i", {
        className: "fas fa-house"
      }), " ", s.room_count || 0), s.allow_self_booking && m("span", {
        className: "ZS-school-tag selfbook"
      }, m("i", {
        className: "fas fa-user-clock"
      }), " \u53EF\u81EA\u4E3B\u7EA6\u8BFE"))), m("div", {
        className: "ZS-school-staff-mobile",
        onclick: this.canManage() ? () => this.openAssignStaff(s) : null
      }, m("i", {
        className: "fas fa-person-chalkboard"
      }), " \u6559\u7EC3 ", s.assigned_coach_count || 0, " \u540D \xB7", ' ', m("i", {
        className: "fas fa-briefcase"
      }), " \u9500\u552E ", s.assigned_sales_count || 0, " \u540D \xB7", ' ', m("i", {
        className: "fas fa-user-tie"
      }), " \u5E97\u957F ", s.assigned_manager_count || 0, " \u540D", this.canManage() ? ' · 点击分配' : ''), m("div", {
        className: "ZS-school-actions-mobile"
      }, m("button", {
        className: "ZS-btn-mini",
        onclick: () => this.openSchedule(s)
      }, "\u8BFE\u8868"), this.canManage() && m("button", {
        className: "ZS-btn-mini",
        onclick: () => this.openEditor(s)
      }, "\u7F16\u8F91"), this.canManage() && m("button", {
        className: "ZS-btn-mini ZS-btn-mini-danger",
        onclick: () => this.remove(s)
      }, "\u5220\u9664"), m("span", {
        style: "flex:1;"
      }), this.canManage() && m('[', null, m("span", {
        className: "ZS-switch-label",
        style: "font-size:11px;"
      }, disabled ? '停用' : '启用'), m(Switch, {
        state: !disabled,
        onchange: checked => this.onSwitchChange(s, checked)
      }))));
    }), this.schools.length === 0 && m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u6682\u65E0\u6821\u533A"))));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/components/SchoolsTab', SchoolsTab);